import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ArrowRight, ChevronDown, FolderGit2, LogOut, Search, Settings2, ShieldCheck, GitBranch, GitPullRequest, BookOpen, UserCircle2 } from 'lucide-react'
import { ProductBrand, ServiceFeatures } from '@aide/ui'
import { authBoundarySummary } from '@aide/auth-client'
import ForgeWorkspaceView from './ForgeWorkspaceView'
import ForgeLibrariesView from './ForgeLibrariesView'
const folioOrigin = 'https://folio.int10h.de'
import { AuthProvider, useAuth } from './hooks/useAuth'
import api, { type WorkspacePublicRepoSummary } from './services/api'

function ProtectedRoute({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { authenticated, loading } = useAuth()

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading Forge…</div>
  if (!authenticated) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  return <>{children}</>
}

function LandingView() {
  const { login, signup } = useAuth()
  const [signupEmail, setSignupEmail] = useState('')
  const [publicRepoSearch, setPublicRepoSearch] = useState('')
  const [publicRepos, setPublicRepos] = useState<WorkspacePublicRepoSummary[]>([])
  const [publicRepoLoading, setPublicRepoLoading] = useState(true)
  const [publicRepoError, setPublicRepoError] = useState(false)
  const mostVisitedRepos = [...publicRepos]
    .filter((repo) => repo.visit_count > 0)
    .sort((left, right) => right.visit_count - left.visit_count || left.full_name.localeCompare(right.full_name))
    .slice(0, 3)
  const highlightedRepos = mostVisitedRepos.length > 0 ? mostVisitedRepos : publicRepos.slice(0, 3)

  async function handlePublicRepoVisit(repo: WorkspacePublicRepoSummary) {
    setPublicRepos((repos) => repos.map((item) => item.repo_id === repo.repo_id ? { ...item, visit_count: item.visit_count + 1 } : item))
    try {
      const tracked = await api.trackPublicWorkspaceRepoVisit(repo.repo_id)
      setPublicRepos((repos) => repos.map((item) => item.repo_id === repo.repo_id ? { ...item, visit_count: tracked.visit_count } : item))
    } catch {
      // Public discovery should still lead to sign-in even if analytics are temporarily unavailable.
    }
  }

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setPublicRepoLoading(true)
      void api.searchPublicWorkspaceRepos(publicRepoSearch.trim())
        .then((repos) => {
          setPublicRepos(repos)
          setPublicRepoError(false)
        })
        .catch(() => {
          setPublicRepos([])
          setPublicRepoError(true)
        })
        .finally(() => setPublicRepoLoading(false))
    }, 160)
    return () => window.clearTimeout(handle)
  }, [publicRepoSearch])

  return (
    <div className="product-site forge-site">
      <div className="product-container">
        <header className="product-nav">
          <Link to="/" aria-label="Forge home"><ProductBrand product="forge" /></Link>
          <nav aria-label="Main navigation"><a className="product-secondary-nav" href="#features">Why Forge</a><a href="#explore">Explore</a><Link to="/login">Log in</Link><Link className="product-button" to="/signup">Sign up <ArrowRight size={15} aria-hidden="true" /></Link></nav>
        </header>
        <main>
          <section className="product-hero">
            <div>
              <span className="product-kicker">A home for code and collaboration</span>
              <h1>Good code.<br /><em>Shared understanding.</em></h1>
              <p className="product-lead">Forge brings repositories, code review, and source knowledge together. A place for the work—and the context that makes it easier to understand.</p>
              <form className="forge-signup" onSubmit={(event) => { event.preventDefault(); void signup('/repositories', signupEmail) }}>
                <label className="sr-only" htmlFor="forge-start-email">Enter your email</label>
                <input id="forge-start-email" type="email" required value={signupEmail} onChange={(event) => setSignupEmail(event.target.value)} placeholder="Your email address" />
                <button className="product-button" type="submit">Sign up for Forge <ArrowRight size={16} aria-hidden="true" /></button>
              </form>
              <p className="product-subtle">Already at home here? <button className="forge-text-button" onClick={() => void login('/repositories')}>Sign in</button></p>
            </div>
            <div className="forge-code-story" aria-label="Illustrative repository example">
              <div className="product-example-label"><span><FolderGit2 size={15} aria-hidden="true" /> controller / firmware</span><span>Illustration</span></div>
              <div className="forge-code-title"><GitPullRequest size={23} aria-hidden="true" /><div><strong>A change with a reason.</strong><span>Calibration for the next controller</span></div></div>
              <div className="forge-code-lines" aria-label="Example code change"><code><span>−</span> calibration: defaultProfile</code><code><span>+</span> calibration: controllerProfile</code><code><span>+</span> verifySensorRange()</code></div>
              <div className="forge-context-note"><BookOpen size={18} aria-hidden="true" /><div><strong>Context beside the code</strong><p>The source note explains which controller needs this calibration—and why.</p></div></div>
              <div className="forge-code-bottom"><span>Source revision</span><span>Author’s note</span><span>Change history</span></div>
            </div>
          </section>
          <section id="features" className="product-section">
            <span className="product-kicker">From first commit to shared work</span><h2>The code is only part of the story.</h2>
            <ServiceFeatures>
              <article><GitBranch aria-hidden="true" /><h3>A home for every project</h3><p>Git repositories, branches, and file history keep the work organized, from private projects to public code.</p></article>
              <article><GitPullRequest aria-hidden="true" /><h3>A place to review together</h3><p>Pull requests bring changes and their descriptions into one view before they become part of the project.</p></article>
              <article><BookOpen aria-hidden="true" /><h3>The reasoning within reach</h3><p>Contextful source knowledge and your notes sit alongside engineering work, with references back to their origins.</p></article>
            </ServiceFeatures>
          </section>
          <section id="explore" className="product-section">
            <div className="forge-explore-heading"><div><span className="product-kicker">Public repositories</span><h2>Work worth exploring.</h2></div><p className="product-subtle">Projects shared by the people building here.</p></div>
            <label className="forge-public-search"><Search size={20} aria-hidden="true" /><input aria-label="Search public work" value={publicRepoSearch} onChange={(event) => setPublicRepoSearch(event.target.value)} placeholder="Search repositories" /></label>
            {!publicRepoSearch && highlightedRepos.length > 0 && <p className="product-subtle forge-results-label">{mostVisitedRepos.length ? 'Popular repositories' : 'Recently shared'}</p>}
            <div className="forge-public-grid">
              {(publicRepoSearch ? publicRepos : highlightedRepos).map((repo) => <Link key={repo.repo_id} to="/login" onClick={() => void handlePublicRepoVisit(repo)} className="forge-public-repo"><FolderGit2 size={19} aria-hidden="true" /><h3>{repo.full_name}</h3><p>{repo.description || 'A public repository on Forge.'}</p><span>{repo.language || repo.default_branch} · Public</span></Link>)}
            </div>
            {publicRepoLoading && <p className="forge-search-status" role="status">Searching public work…</p>}
            {!publicRepoLoading && publicRepoError && <p className="forge-search-status" role="status">Public search is unavailable right now.</p>}
            {!publicRepoLoading && !publicRepoError && publicRepos.length === 0 && <p className="forge-search-status" role="status">{publicRepoSearch ? 'No public work matches that search.' : 'Public projects will appear here as they are shared.'}</p>}
          </section>
        </main>
        <footer className="product-footer"><ProductBrand product="forge" /><span>Code and context, in good company.</span><Link to="/login">Your repositories <span aria-hidden="true">↗</span></Link></footer>
      </div>
    </div>
  )
}

function AuthPage({ mode }: { mode: 'login' | 'signup' }) {
  const { login, signup } = useAuth()
  const location = useLocation()
  const nextPath = typeof location.state?.from === 'string' ? location.state.from : '/repositories'
  const isSignup = mode === 'signup'
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  return (
    <div className="product-site"><div className="product-container">
      <header className="product-nav"><Link to="/" aria-label="Forge home"><ProductBrand product="forge" /></Link><Link to="/">Back to Forge</Link></header>
      <main className="product-hero">
        <div><span className="product-kicker">Code and context, together</span><h1>{isSignup ? <>Your next project<br /><em>has a home.</em></> : <>Back to<br /><em>building.</em></>}</h1><p className="product-lead">Your repositories, reviews, and the knowledge behind them. Together in Forge.</p></div>
        <form className="product-auth" onSubmit={async (event) => {
          event.preventDefault(); setError(''); setSubmitting(true)
          try { await (isSignup ? signup(nextPath, email) : login(nextPath, email)) }
          catch (err) { setError(err instanceof Error ? err.message : 'Sign-in is unavailable. Please try again.') }
          finally { setSubmitting(false) }
        }}>
          <ProductBrand product="forge" compact /><h2>{isSignup ? 'Create your Forge account' : 'Welcome back'}</h2>
          <p>{isSignup ? 'A place for your work, and the people you build with.' : 'Your projects are ready when you are.'}</p>
          <label className="forge-auth-label" htmlFor="forge-email">{isSignup ? 'Email' : 'Email or username'}</label><input className="forge-auth-input" id="forge-email" type={isSignup ? 'email' : 'text'} required value={email} onChange={(event) => setEmail(event.target.value)} placeholder={isSignup ? 'you@company.com' : 'you@company.com or username'} autoComplete={isSignup ? 'email' : 'username'} />
          {error && <div role="alert" className="product-alert">{error}</div>}
          <button className="product-button" type="submit" disabled={submitting}>{submitting ? 'Redirecting…' : isSignup ? 'Continue to sign up' : 'Continue to sign in'}<ArrowRight size={16} aria-hidden="true" /></button>
          <p className="product-subtle">One account across Forge and Contextful.</p>
          <p style={{ marginTop: 24 }}>{isSignup ? 'Already have an account?' : 'New to Forge?'} <Link className="forge-text-button" to={isSignup ? '/login' : '/signup'} state={{ from: nextPath }}>{isSignup ? 'Log in' : 'Sign up'}</Link></p>
        </form>
      </main>
    </div></div>
  )
}

function RepoShell({ librariesMode = false }: { librariesMode?: boolean }) {
  const { user, logout, profile } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [headerSearch, setHeaderSearch] = useState('')
  const menuRef = useRef<HTMLDivElement | null>(null)
  const roles = profile?.roles || []
  const boundary = authBoundarySummary(profile)
  const isOrgAdmin = roles.includes('admin') || roles.includes('org_admin') || roles.includes('organization_admin') || boundary.adminScopeLabel === 'Contextful admin' || boundary.adminScopeLabel === 'Tenant admin' || boundary.adminScopeLabel === 'Organization admin'

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('click', handleClick)
    window.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('click', handleClick)
      window.removeEventListener('keydown', handleEscape)
    }
  }, [])

  return (
    <div className="min-h-screen bg-[var(--forge-bg)] text-[var(--forge-text)]">
      <header className="relative z-40 border-b border-[var(--forge-border)] bg-[var(--forge-panel)]/95 text-[var(--forge-text)]">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-6 px-6 py-2.5 lg:px-8">
          <div className="flex min-w-0 items-center gap-4">
            <Link to="/repositories" className="flex items-center gap-3">
              <ProductBrand product="forge" />
            </Link>
            <div className="hidden min-w-[260px] max-w-[320px] flex-1 md:block">
              <label className="flex items-center gap-2 rounded-md border border-[var(--forge-border)] bg-[var(--forge-panel-subtle)] px-3 py-1.5 text-sm text-[var(--forge-text-muted)]">
                <Search className="h-4 w-4" />
                <input
                  aria-label="Header search"
                  value={headerSearch}
                  onChange={(e) => setHeaderSearch(e.target.value)}
                  placeholder="Search repositories"
                  className="w-full bg-transparent text-[var(--forge-text)] outline-none placeholder:text-[var(--forge-text-muted)]"
                />
              </label>
            </div>
            <nav className="hidden items-center gap-5 text-sm text-[var(--forge-text-muted)] md:flex">
              <a href={folioOrigin} className="hover:text-[var(--forge-text)]">Folio</a>
              <NavLink to="/repositories" className={({ isActive }) => isActive ? 'font-medium text-[var(--forge-text)]' : 'hover:text-[var(--forge-text)]'} end>
                Repositories
              </NavLink>
              <NavLink to="/libraries" className={({ isActive }) => isActive ? 'font-medium text-[var(--forge-text)]' : 'hover:text-[var(--forge-text)]'}>Libraries</NavLink>
              <NavLink to="/settings/personal" className={({ isActive }) => isActive ? 'font-medium text-[var(--forge-text)]' : 'hover:text-[var(--forge-text)]'}>
                <span className="inline-flex items-center gap-2"><Settings2 className="h-4 w-4" /> Settings</span>
              </NavLink>
            </nav>
          </div>
          <div ref={menuRef} className="relative flex items-center gap-4 text-sm text-[var(--forge-text-muted)]">
            <div className="hidden items-center gap-2 rounded-md border border-[var(--forge-border)] bg-transparent px-3 py-1.5 lg:flex" title={`Forge boundary: ${boundary.tenantLabel}`}>
              <ShieldCheck className="h-4 w-4 text-[var(--forge-accent)]" />
              <div className="min-w-0">
                <div className="max-w-[180px] truncate text-xs font-medium text-[var(--forge-text)]">{boundary.tenantLabel}</div>
                <div className="max-w-[180px] truncate text-[11px] text-[var(--forge-text-muted)]">{boundary.organizationLabel || boundary.appTenantLabel || 'Forge workspace'}</div>
              </div>
              <span className="rounded bg-[var(--forge-accent-soft)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--forge-accent)]">{boundary.adminScopeLabel}</span>
            </div>
            <button
              onClick={(event) => {
                event.stopPropagation()
                setMenuOpen((current) => !current)
              }}
              className="inline-flex items-center gap-2 rounded-md border border-[var(--forge-border)] bg-[var(--forge-panel)] px-3 py-1.5 text-sm font-medium text-[var(--forge-text)] transition hover:bg-[var(--forge-panel-subtle)]"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <UserCircle2 className="h-4 w-4" />
              <span>{user || 'Signed in'}</span>
              <ChevronDown className="h-4 w-4 text-[var(--forge-text-muted)]" />
            </button>
            {menuOpen ? (
              <div className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-md border border-[var(--forge-border)] bg-[var(--forge-panel)] shadow-lg" role="menu">
                <div className="border-b border-[var(--forge-border)] px-4 py-3">
                  <div className="text-sm font-medium text-[var(--forge-text)]">{user || 'Signed in'}</div>
                  <div className="mt-1 text-xs text-[var(--forge-text-muted)]">{profile?.email || 'Forge account'}</div>
                  <div className="mt-3 rounded-md border border-[var(--forge-border)] bg-[var(--forge-panel-subtle)] px-3 py-2 text-xs">
                    <div className="font-medium text-[var(--forge-text)]">{boundary.tenantLabel}</div>
                    <div className="mt-0.5 text-[var(--forge-text-muted)]">{boundary.organizationLabel || 'No organization claim'} · {boundary.appLabel}</div>
                    <div className="mt-0.5 text-[var(--forge-text-muted)]">{boundary.appTenantLabel || 'No app tenant id'}</div>
                  </div>
                </div>
                <div className="px-2 py-2">
                  <div className="px-2 py-2 text-[11px] font-semibold text-[var(--forge-text-muted)]">Settings</div>
                  <Link to="/settings/personal" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-[var(--forge-text)] transition hover:bg-[var(--forge-panel-subtle)]">
                    <UserCircle2 className="h-4 w-4" /> Personal settings
                  </Link>
                  {isOrgAdmin ? (
                    <Link to="/settings/organization" onClick={() => setMenuOpen(false)} className="mt-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm text-[var(--forge-text)] transition hover:bg-[var(--forge-panel-subtle)]">
                      <Settings2 className="h-4 w-4" /> Organization settings
                    </Link>
                  ) : null}
                </div>
                <div className="border-t border-[var(--forge-border)] px-2 py-2">
                  <button onClick={() => void logout()} className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-[var(--forge-text)] transition hover:bg-[var(--forge-panel-subtle)]">
                    <LogOut className="h-4 w-4" /> Log out
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </header>
      <nav aria-label="Mobile Forge navigation" className="flex gap-5 border-b border-[var(--forge-border)] px-6 py-3 text-sm md:hidden"><Link to="/repositories">Repositories</Link><Link to="/libraries">Libraries</Link><a href={folioOrigin}>Folio</a></nav>
      <main className="mx-auto max-w-[1280px] px-6 py-5 lg:px-8">
        {librariesMode ? <ForgeLibrariesView /> : <ForgeWorkspaceView />}
      </main>
    </div>
  )
}

function FolioRedirect({ path = '/' }: { path?: string }) {
  useEffect(() => { window.location.replace(folioOrigin + path) }, [path])
  return <p role="status">Opening Folio… <a href={folioOrigin + path}>Continue to Folio</a></p>
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingView />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/signup" element={<AuthPage mode="signup" />} />
      <Route path="/office/login" element={<FolioRedirect path="/login" />} />
      <Route path="/office/signup" element={<FolioRedirect path="/signup" />} />
      <Route path="/office" element={<FolioRedirect />} />
      <Route path="/repositories" element={<ProtectedRoute><RepoShell /></ProtectedRoute>} />
      <Route path="/libraries" element={<ProtectedRoute><RepoShell librariesMode /></ProtectedRoute>} />
      <Route path="/repositories/:repoId" element={<ProtectedRoute><RepoShell /></ProtectedRoute>} />
      <Route path="/repositories/:repoId/:tab" element={<ProtectedRoute><RepoShell /></ProtectedRoute>} />
      <Route path="/settings/organization" element={<ProtectedRoute><RepoShell /></ProtectedRoute>} />
      <Route path="/settings/personal" element={<ProtectedRoute><RepoShell /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return <AuthProvider><AppRoutes /></AuthProvider>
}
